import React, { useEffect, useState } from "react"
import { Button, Card, CardBody, Col, Container, Row, Table } from "reactstrap"
import { useTranslation } from "react-i18next"
import { Pencil, Trash } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"

import Spinners from "components/Shared/Spinners"
import NoDataAvailable from "components/Shared/NoDataAvailable"
import Paginations from "components/Common/Pagination"
import { handleStopPropagation } from "components/Common/Utils"

import DeleteModal from "components/Common/DeleteModal"
import { showAlert } from "store/reducers/alertReducer"
import {
  deleteTrainingMaterial,
  getAllTrainingMaterials,
} from "store/actions/trainingMaterialAction"
import { usePermissions } from "hooks"

const TrainingMaterials = () => {
  const navigate = useNavigate()
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const dispatch = useDispatch()

  const {
    loading,
    isLoading,
    perPage,
    totalPages,
    totalCount,
    currentPage,
    trainingMaterials,
  } = useSelector(state => state.TrainingMaterials)
  const { permissions, showAll } = usePermissions("training_materials")

  const { search } = useLocation()
  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  useEffect(() => {
    getAllData()
  }, [])

  const getAllData = page => {
    const payload = {
      limit: perPage,
      page_number: page || pageNumber,
      order_by: 1,
      filters: {
        keyword: "",
        date_range: {
          // "start_date": "2025-02-10",
          // "end_date": "2025-02-26"
        },
      },
    }

    dispatch(getAllTrainingMaterials(payload))
    navigate(`/training-materials?page=${page || pageNumber}`)
  }

  const onPageChange = page => {
    getAllData(page)
  }

  const handleDelete = () => {
    dispatch(deleteTrainingMaterial(itemIdToDelete))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          getAllData()
          setShowDeleteModal(false)
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <React.Fragment>
      <div className="page-content">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <Col xl="12">
                <div className="d-flex justify-content-between mb-4 align-items-center">
                  <h4 className="card-title">{t("Training Materials")}</h4>
                  {(showAll || permissions.includes("can_create")) && (
                    <div>
                      <button
                        className="main_btn"
                        onClick={() => navigate("/training-materials/create")}
                      >
                        {t("Add New")}
                      </button>
                    </div>
                  )}
                </div>
                <Card className="user_personal_info theme_card overflow-x-auto">
                  <CardBody>
                    {!loading && trainingMaterials.length === 0 ? (
                      <NoDataAvailable />
                    ) : (
                      <div className="table-responsive">
                        <Table className="table mb-0">
                          <thead className="table-light full_th">
                            <tr>
                              <th>{t("S. No.")}</th>
                              <th>{t("Name")}</th>
                              <th>{t("Contents")}</th>
                              <th>{t("Action")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trainingMaterials?.map((item, index) => (
                              <tr key={index}>
                                <th scope="row">
                                  {(currentPage - 1) * perPage + index + 1}
                                </th>
                                <td>{item?.name}</td>
                                <td>
                                  {item?.contents ? (
                                    <a
                                      href={
                                        language === "sa"
                                          ? item?.contents_ar
                                          : item?.contents
                                      }
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-decoration-underline"
                                    >
                                      <i
                                        className="bx bxs-file-pdf"
                                        style={{ fontSize: 25 }}
                                        title={t("View PDF")}
                                      ></i>
                                    </a>
                                  ) : (
                                    "-"
                                  )}
                                </td>

                                <td onClick={handleStopPropagation}>
                                  <div className="d-flex gap-2 action_buttons">
                                    {(showAll ||
                                      permissions.includes("can_edit")) && (
                                      <Button
                                        color="secondary"
                                        onClick={() => {
                                          navigate(
                                            `/trainingMaterials/update?uuid=${item?.uuid}`
                                          )
                                        }}
                                        title={t("Update")}
                                      >
                                        <Pencil />
                                      </Button>
                                    )}
                                    {(showAll ||
                                      permissions.includes("can_delete")) && (
                                      <Button
                                        color="danger"
                                        onClick={() => {
                                          setItemIdToDelete(item.uuid)
                                          setShowDeleteModal(true)
                                        }}
                                        disabled={
                                          isLoading &&
                                          item?.uuid === itemIdToDelete
                                        }
                                        title={t("Delete")}
                                      >
                                        <Trash />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
                {!loading && trainingMaterials.length > 0 && (
                  <Paginations
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    onPageChange={onPageChange}
                  />
                )}
              </Col>
            </Row>
          </Container>
        )}
      </div>
      <DeleteModal
        show={showDeleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setItemIdToDelete(null)
          setShowDeleteModal(false)
        }}
        isLoading={isLoading}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this material?")}
      />
    </React.Fragment>
  )
}

export default TrainingMaterials
